import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldArray, useFormikContext } from 'formik';

import { ReactComponent as AddButton } from '../../../../../assets/images/AddButton.svg';
import { ReactComponent as CrossIcon } from '../../../../../assets/images/CrossIcon.svg';
import { ReactComponent as EmailFormIcon } from '../../../../../assets/images/EmailFormIcon.svg';
import CustomTextField from '../../../../../components/shared/CustomTextField/CustomTextField';
import { CONTACT_EMAIL_TYPES } from '../../../../../constants/commonConstants';
import { filterData } from '../../../../../helpers/commonHelper';
import ContactFormSelect from '../ContactFormSelect/ContactFormSelect';

import styles from '../AddContactModal.module.scss';

const ContactFormEmailWeb = ({ isEdit }) => {
  const { t } = useTranslation('common');
  const { values, setValues } = useFormikContext();

  const lastElement = useMemo(() => {
    const filteredData = filterData(values.user_contact_emails_attributes);
    return filteredData[filteredData.length - 1];
  }, [values]);

  const handleRemoveField = useCallback(
    (arrayHelpers, index) => {
      if (values.user_contact_emails_attributes.length === 1) {
        arrayHelpers.replace(index, { email: '', email_type: '' });
      } else {
        arrayHelpers.remove(index);
      }
    },
    [values]
  );

  const handleEditRemoveField = useCallback(
    (arrayHelpers, index) => {
      const newData = [...values.user_contact_emails_attributes];
      if (newData[index]?.id) {
        newData[index] = { ...newData[index], _destroy: true };
        setValues({ ...values, user_contact_emails_attributes: newData });
      }
      const data = filterData(newData);
      if (data.length === 0) {
        arrayHelpers.insert(index + 1, { email: '', email_type: '' });
      }
      if (!newData[index].id) {
        if (data.length === 1) {
          arrayHelpers.replace(index, { email: '', email_type: '' });
        } else {
          arrayHelpers.remove(index);
        }
      }
    },
    [values]
  );

  const handleTypeChange = useCallback(
    (option, index, arrayHelpers) => {
      const newData = [...values.user_contact_emails_attributes];
      newData[index] = { ...newData[index], email_type: option.label };
      arrayHelpers.replace(index, newData[index]);
    },
    [values]
  );

  return (
    <div>
      <FieldArray
        name="user_contact_emails_attributes"
        render={(arrayHelpers) => (
          <div className={styles.container}>
            {values.user_contact_emails_attributes?.map((value, index) => {
              const { _destroy } = value;
              if (!_destroy) {
                return (
                  <div className={styles.multiInputWrapper}>
                    <div className={styles.multiInputContainer}>
                      <div className={styles.multiInputField}>
                        <Field
                          name={`user_contact_emails_attributes[${index}].email`}
                          as={CustomTextField}
                          value={value.email}
                          type="text"
                          label={t('formLabels.emailAddress')}
                          CustomIcon={EmailFormIcon}
                        />
                      </div>
                      {value.email?.length > 0 && (
                        <div className={styles.multiSelectField}>
                          <Field
                            name={`user_contact_emails_attributes[${index}].email_type`}
                            as={ContactFormSelect}
                            data={value.email_type || 'Work'}
                            placeholder={t('placeholders.label')}
                            options={CONTACT_EMAIL_TYPES}
                            handleOnChange={(option) => handleTypeChange(option, index, arrayHelpers)}
                          />
                        </div>
                      )}
                    </div>
                    <div
                      className={value.email?.length > 0 ? styles.showIconButtonContainer : styles.iconButtonContainer}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          isEdit ? handleEditRemoveField(arrayHelpers, index) : handleRemoveField(arrayHelpers, index)
                        }
                        className={styles.iconButton}
                      >
                        <CrossIcon />
                      </button>
                      {value.email?.length > 0 && values.user_contact_emails_attributes[index] === lastElement && (
                        <button
                          className={styles.iconButton}
                          type="button"
                          onClick={() => arrayHelpers.insert(index + 1, { email: '', email_type: '' })}
                        >
                          <AddButton />
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      />
    </div>
  );
};

export default ContactFormEmailWeb;
